import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { AppHeader } from "../../../components/app-header";
import { Button } from "../../../components/ui/button";
import { ConfirmDialog } from "../../../components/ui/dialog";
import { InlineError, Skeleton } from "../../../components/ui/feedback";
import { Input } from "../../../components/ui/input";
import { Select } from "../../../components/ui/select";
import { notify } from "../../../components/ui/toast";
import { account } from "../../../copy/home";
import { api, errorMessage } from "../../../lib/api";
import { useDelayedFlag } from "../../../lib/hooks";
import { useOnline } from "../../../lib/online";
import { meQuery, qk } from "../../../lib/queries";
import { signOutAndGo } from "../../../lib/session";

export const Route = createFileRoute("/_authed/account/delete")({
  component: DeleteAccountPage,
});

const DELETE = "__delete__";

/** Delete my account (UX_SPEC §11): decide per owned app, then type your email. */
function DeleteAccountPage() {
  const me = useQuery(meQuery);
  const online = useOnline();
  const plan = useQuery({ queryKey: qk.deletionPlan, queryFn: ({ signal }) => api.getDeletionPlan(signal) });
  const showSkeleton = useDelayedFlag(plan.isPending, 150);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const copy = account.delete;
  const email = me.data?.user.email ?? "";
  const matches = typed.trim().toLowerCase() === email.toLowerCase() && email !== "";

  const del = useMutation({
    mutationFn: () => {
      const transfers = (plan.data?.ownedApps ?? []).flatMap((app) => {
        const choice = choices[app.id] ?? app.editors[0]?.id ?? DELETE;
        return choice === DELETE ? [] : [{ appId: app.id, toUserId: choice }];
      });
      return api.deleteMe({ confirmEmail: typed.trim(), transfers });
    },
    onSuccess: () => {
      notify.success(copy.done);
      void signOutAndGo();
    },
    onError: (e) => notify.error(errorMessage(e)),
  });

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main id="main" className="mx-auto max-w-2xl px-4 pt-8 pb-16 sm:px-6">
        <h1 className="text-2xl font-semibold">{copy.pageTitle}</h1>
        <p className="mt-1 mb-6 text-sm text-muted-foreground">{copy.intro}</p>
        {plan.isPending ? (
          showSkeleton ? (
            <Skeleton className="h-24 w-full" />
          ) : null
        ) : plan.isError ? (
          <InlineError message={copy.loadFailed} onRetry={() => void plan.refetch()} />
        ) : (
          <section aria-labelledby="owned-apps">
            <h2 id="owned-apps" className="mb-3 text-base font-semibold">
              {copy.ownedApps}
            </h2>
            {plan.data.ownedApps.length === 0 ? (
              <p className="text-sm text-muted-foreground">{copy.noOwnedApps}</p>
            ) : (
              <ul className="divide-y divide-border rounded-xl border border-border">
                {plan.data.ownedApps.map((app) => {
                  const options = [...app.editors.map((e) => ({ value: e.id, label: copy.giveTo(e.name) })), { value: DELETE, label: copy.deleteIt }];
                  const value = choices[app.id] ?? app.editors[0]?.id ?? DELETE;
                  return (
                    <li key={app.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm font-medium">{app.name}</span>
                      {app.editors.length > 0 ? (
                        <Select value={value} onValueChange={(v) => setChoices((c) => ({ ...c, [app.id]: v }))} options={options} label={copy.whatFor(app.name)} size="sm" />
                      ) : (
                        <span className="max-w-xs text-xs text-muted-foreground">{copy.noEditors}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-6 flex justify-end">
              <Button variant="danger" disabled={!online} onClick={() => setConfirmOpen(true)}>
                {copy.continue}
              </Button>
            </div>
          </section>
        )}
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(o) => {
            setConfirmOpen(o);
            if (!o) setTyped("");
          }}
          title={copy.confirmTitle}
          description={copy.confirmBody}
          confirmLabel={copy.confirm}
          busyLabel={copy.deleting}
          busy={del.isPending}
          confirmDisabled={!matches || !online}
          onConfirm={() => del.mutate()}
        >
          <label htmlFor="confirm-email" className="mt-4 block text-sm font-medium">
            {copy.typeEmail(email)}
          </label>
          <Input id="confirm-email" className="mt-1.5" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} aria-invalid={typed !== "" && !matches ? true : undefined} />
          {typed !== "" && !matches && <p className="mt-1 text-xs text-muted-foreground">{copy.mismatch}</p>}
        </ConfirmDialog>
      </main>
    </div>
  );
}
